import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, ContextualMenu, showNotification } from 'sonata-react-components';

import { PromptModal } from '@/components/common/PromptModal';
import { bulkDeleteLineItems, bulkDuplicateLineItems } from '@/services/lineItemId.service';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';

const BULK_ACTION_MAX_ITEMS = 100;

type Props = {
  onBulkActionComplete?: () => void;
};

export default function MultiLinesSelectionMenu({ onBulkActionComplete }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [status, setStatus] = useState<'closed' | 'open'>('closed');
  const [showDeletionPrompt, setShowDeletionPrompt] = useState(false);
  const { selectedLineItems, setCacheSeed, setSelectedLineItems } = useCampaignStore() as CampaignsStore;
  if (selectedLineItems.length <= 1) {
    return null;
  }

  const lineItemsIds = selectedLineItems.map(lt => lt.id).join(',');
  const reportingURL = `${taptap.config.sonataUrl}app.html#!/reporting/${lineItemsIds}`;
  const onClick = () => {
    window.location.href = reportingURL;
  };

  const driveToStoreReportingHref = `reporting/driveToStore/lineItem/${lineItemsIds}`;
  const onDriveToStoreClick = () => {
    window.open(driveToStoreReportingHref, '_blank', 'noopener,noreferrer');
  };

  const showAggregatedReportOption = selectedLineItems.every(li => li.isDriveToStoreEligible);

  const showTooManySelectedError = () => {
    showNotification(
      'error',
      formatMessage(
        {
          defaultMessage: 'You can only select up to {max} Line Items for this action',
          id: 'lineItems.multiSelection.tooManySelected'
        },
        { max: BULK_ACTION_MAX_ITEMS }
      )
    );
  };

  const handleDuplicate = async () => {
    const ids = selectedLineItems.map(li => li.id);
    if (ids.length > BULK_ACTION_MAX_ITEMS) {
      return showTooManySelectedError();
    }
    const { results } = await bulkDuplicateLineItems(ids);
    const failed = results.filter(result => !result.success).length;
    setCacheSeed();
    setSelectedLineItems([]);
    onBulkActionComplete?.();
    if (failed === 0) {
      showNotification(
        'success',
        formatMessage(
          { defaultMessage: '{count} Line Items duplicated successfully', id: 'lineItems.multiSelection.duplicated' },
          { count: ids.length }
        )
      );
    } else {
      showNotification(
        'error',
        formatMessage(
          {
            defaultMessage: '{succeeded} of {total} Line Items duplicated, {failed} failed',
            id: 'lineItems.multiSelection.duplicatedPartial'
          },
          { failed, succeeded: ids.length - failed, total: ids.length }
        )
      );
    }
  };

  const handleConfirmDeletion = async () => {
    setShowDeletionPrompt(false);
    const ids = selectedLineItems.map(li => li.id);
    if (ids.length > BULK_ACTION_MAX_ITEMS) {
      return showTooManySelectedError();
    }
    const { results } = await bulkDeleteLineItems(ids);
    const failed = results.filter(result => !result.success).length;
    setCacheSeed();
    setSelectedLineItems([]);
    onBulkActionComplete?.();
    if (failed === 0) {
      showNotification(
        'success',
        formatMessage(
          { defaultMessage: '{count} Line Items deleted successfully', id: 'lineItems.multiSelection.deleted' },
          { count: ids.length }
        )
      );
    } else {
      showNotification(
        'error',
        formatMessage(
          {
            defaultMessage: '{succeeded} of {total} Line Items deleted, {failed} failed',
            id: 'lineItems.multiSelection.deletedPartial'
          },
          { failed, succeeded: ids.length - failed, total: ids.length }
        )
      );
    }
  };

  return (
    <div>
      {showDeletionPrompt && (
        <PromptModal
          cancelCallback={() => setShowDeletionPrompt(false)}
          confirmCallback={handleConfirmDeletion}
          texts={{
            buttons: {
              cancel: formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' }),
              confirm: formatMessage({ defaultMessage: 'Confirm', id: 'common.confirm' })
            },
            description: formatMessage(
              {
                defaultMessage:
                  "Deleting the {count} selected line items can't be undone and all associated information will be permanently lost.",
                id: 'lineItems.multiSelection.prompts.deletion.description'
              },
              { count: selectedLineItems.length }
            ) as string,
            title: formatMessage(
              {
                defaultMessage: 'Are you sure you want to delete the {count} selected line items?',
                id: 'lineItems.multiSelection.prompts.deletion.title'
              },
              { count: selectedLineItems.length }
            )
          }}
          variant="warning"
        />
      )}
      <ContextualMenu id="multiLinesSelectionMenu" placement="bottom">
        <ContextualMenu.Trigger>
          <ButtonUI
            icon="fa-solid fa-list-ul"
            onClick={() => setStatus(status === 'open' ? 'closed' : 'open')}
            shape="squareRounded"
            size="medium"
            text={formatMessage({
              defaultMessage: 'Bulk actions',
              id: 'lineItems.multiSelection.button'
            })}
            type="secondary"
          />
        </ContextualMenu.Trigger>
        <ContextualMenu.Item
          icon="fa-solid fa-chart-column"
          onClick={onClick}
          subtitle={formatMessage({
            defaultMessage: 'Campaign metrics',
            id: 'lineItems.multiSelection.deliveryReportSubtitle'
          })}
          text={formatMessage({
            defaultMessage: 'Delivery Report',
            id: 'lineItems.multiSelection.deliveryReport'
          })}
        />
        {showAggregatedReportOption && (
          <ContextualMenu.Item
            icon="fa-solid fa-chart-mixed"
            onClick={onDriveToStoreClick}
            subtitle={formatMessage({
              defaultMessage: 'Attribution by all POIs',
              id: 'lineItems.multiSelection.aggregatedReportSubtitle'
            })}
            text={formatMessage({
              defaultMessage: 'Multi-Store Report',
              id: 'lineItems.multiSelection.aggregatedReport'
            })}
          />
        )}
        <ContextualMenu.Item
          icon="fa fa-files-o"
          onClick={handleDuplicate}
          text={formatMessage({ defaultMessage: 'Duplicate', id: 'lineItems.multiSelection.duplicate' })}
        />
        <ContextualMenu.Item
          icon="fa-regular fa-trash"
          onClick={() => setShowDeletionPrompt(true)}
          text={formatMessage({ defaultMessage: 'Delete', id: 'lineItems.multiSelection.delete' })}
        />
      </ContextualMenu>
    </div>
  );
}
